import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { BadgeRankingIcon } from '@/assets/badge-ranking-icon.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.tsx';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area.tsx';
import PlaceholderPlantImage from '@/assets/placeholder-plant.jpg';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { cn } from '@/utils/utils.ts';
import style from './plant-ranking.module.scss';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LastUpdateTimestamp } from '@/components/last-update-timestamp.tsx';
import {
  PlantsRankingItemResponse,
  useGetPlantsRankingQuery,
} from '@/features/plants/hooks/queries/use-get-plants-ranking.ts';

const EMPTY_DATA: PlantsRankingItemResponse[] = [];

export const PlantRanking = ({ className }: { className?: string }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0, rootMargin: '20px' });

  const {
    data: plantRanking,
    isPending,
    dataUpdatedAt: plantRankingUpdatedAt,
  } = useGetPlantsRankingQuery({ enabled: inView });

  const { t } = useTranslation();

  const columns = useMemo(
    (): ColumnDef<PlantsRankingItemResponse>[] => [
      {
        id: 'rank',
        header: () => t('dashboard.plant_ranking.columns.rank'),
        cell: ({ row }) => {
          const rank = row.index + 1;
          return (
            <div className="w-[55px] p-0 font-medium">
              {rank <= 3 ? (
                <div className="flex w-[36px] items-center justify-center">
                  <RankBadge rank={rank} />
                </div>
              ) : (
                <span className="flex w-[36px] items-center justify-center text-base">
                  {rank}
                </span>
              )}
            </div>
          );
        },
        size: 40,
        meta: {
          headerClassName: 'w-[40px] p-1',
        },
      },
      {
        id: 'image',
        accessorKey: 'signedImageUrl',
        header: () => t('dashboard.plant_ranking.columns.image'),
        cell: (info) => {
          const imageUrl = info.getValue<string>();
          return (
            <div className="h-[60px] w-[70px]">
              <img
                src={imageUrl || PlaceholderPlantImage}
                className="aspect-square w-11 rounded-full object-cover"
                alt="Plant"
              />
            </div>
          );
        },
        meta: {
          headerClassName: '',
        },
      },
      {
        id: 'name',
        accessorKey: 'plantName',
        header: () => t('dashboard.plant_ranking.columns.name'),
        cell: (info) => {
          const plantName = info.getValue<string>();
          const plantId = info.row.original.plantId;
          return (
            <Link className="link-underline text-base text-blue-600" to={`/plants/${plantId}`}>
              {plantName}
            </Link>
          );
        },
        meta: {
          headerClassName: '',
        },
      },
      {
        id: 'storageCapacity',
        accessorKey: 'storageCapacity',
        header: () => (
          <div className="text-start">
            <span className="block">{t('metrics.storage_capacity')}</span>
            <span>(kWh)</span>
          </div>
        ),
        cell: (info) => (
          <div className={cn(style.tableCell_totalCapacity, 'text-base')}>
            {formatNumber(info.getValue<string>())}
          </div>
        ),
        meta: {
          headerClassName: cn(style.tableHead_totalCapacity, ' text-start'),
        },
      },
      {
        id: 'chargedEnergy',
        accessorKey: 'chargedEnergy',
        header: () => (
          <div className="py-1 text-start">
            <span className="block">{t('metrics.total_battery_charge')}</span>
            <span>(MWh)</span>
          </div>
        ),
        cell: (info) => (
          <div className={cn(style.tableCell_totalYield, 'text-base')}>
            {convertAndFormatEnergy(info.getValue<string>()).formattedValue}
          </div>
        ),
        meta: {
          headerClassName: cn(style.tableHead_totalYield, ' text-start'),
        },
      },
      {
        id: 'dischargedEnergy',
        accessorKey: 'dischargedEnergy',
        header: () => (
          <div className="text-start">
            <span className="block">{t('metrics.total_battery_discharge')}</span>
            <span>(MWh)</span>
          </div>
        ),
        cell: (info) => (
          <div className={cn(style.tableCell_todayYield, 'text-base')}>
            {convertAndFormatEnergy(info.getValue<string>()).formattedValue}
          </div>
        ),
        meta: {
          headerClassName: cn(style.tableHead_todayYield, ' text-start'),
        },
      },
      {
        id: 'energyThroughput',
        accessorKey: 'energyThroughput',
        header: () => (
          <div className="text-right">
            <span className="block">{t('metrics.energy_throughput')}</span>
            <span>(MWh)</span>
          </div>
        ),
        cell: (info) => (
          <div className="text-right text-base">
            {convertAndFormatEnergy(info.getValue<string>()).formattedValue}
          </div>
        ),
        meta: {
          headerClassName: ' text-right',
        },
      },
    ],
    [t]
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: plantRanking ?? EMPTY_DATA,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <DashboardCard
      ref={ref}
      isLoading={isPending}
      className={cn(style.plant_ranking, 'max-h-[400px] w-full gap-1', className)}
    >
      <>
        <div className="flex w-full flex-col justify-between">
          <h3 className="font-semibold text-nowrap">{t('dashboard.plant_ranking.title')}</h3>
          <LastUpdateTimestamp updatedAt={plantRankingUpdatedAt} />
        </div>
        <ScrollArea className="flex h-full w-full">
          <Table>
            <TableHeader className="bg-background sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'bg-white',
                        (
                          header.column.columnDef.meta as {
                            headerClassName?: string;
                          }
                        )?.headerClassName
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="h-[50px] border-none">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </>
    </DashboardCard>
  );
};

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <BadgeRankingIcon className="text-[#EBA53C]" />;
  if (rank === 2) return <BadgeRankingIcon className="text-[#4D9550]" />;
  if (rank === 3) return <BadgeRankingIcon className="text-[#666666]" />;

  return <div></div>;
}

function formatNumber(value: number | string) {
  return typeof value === 'number' ? new Intl.NumberFormat().format(value) : value;
}

function convertAndFormatEnergy(value: number | string | undefined) {
  if (value === undefined || value === null) {
    return {
      value: 0,
      formattedValue: '-',
      unit: 'MWh',
    };
  }

  if (typeof value === 'string') {
    const numericValue = parseFloat(value);

    if (isNaN(numericValue)) {
      return {
        value: value,
        formattedValue: value,
        unit: 'MWh',
      };
    }

    const mwhValue = numericValue / 1000;
    return {
      value: mwhValue,
      formattedValue: formatNumber(parseFloat(mwhValue.toFixed(3))),
      unit: 'MWh',
    };
  }

  if (typeof value === 'number') {
    const mwhValue = value / 1000;
    return {
      value: mwhValue,
      formattedValue: formatNumber(parseFloat(mwhValue.toFixed(3))),
      unit: 'MWh',
    };
  }

  return {
    value: value,
    formattedValue: String(value),
    unit: 'MWh',
  };
}
